import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtMoney } from '../../lib/currency';
import { toDateStr } from '../../lib/uiStore';
import { useLang, localName } from '../../lib/lang';
import { useStatusName } from '../../lib/localized';
import type { Report, ReportGroupBy, ReportRow } from '../../lib/types';

const DIMENSIONS: Array<{ key: ReportGroupBy; en: string; ar: string }> = [
  { key: 'day', en: 'By day', ar: 'حسب اليوم' },
  { key: 'employee', en: 'By employee', ar: 'حسب الموظف' },
  { key: 'service', en: 'By service', ar: 'حسب الخدمة' },
  { key: 'status', en: 'By status', ar: 'حسب الحالة' },
  { key: 'source', en: 'By source', ar: 'حسب المصدر' },
  { key: 'customer', en: 'By customer', ar: 'حسب العميل' },
];

// Short column header per dimension (the first table column).
const COL_LABEL: Record<ReportGroupBy, { en: string; ar: string }> = {
  day: { en: 'Day', ar: 'اليوم' },
  employee: { en: 'Employee', ar: 'الموظف' },
  service: { en: 'Service', ar: 'الخدمة' },
  status: { en: 'Status', ar: 'الحالة' },
  source: { en: 'Source', ar: 'المصدر' },
  customer: { en: 'Customer', ar: 'العميل' },
};

const PRESETS: Array<{ key: 'today' | 'week' | 'month' | 'custom'; en: string; ar: string }> = [
  { key: 'today', en: 'Today', ar: 'اليوم' },
  { key: 'week', en: '7 days', ar: '7 أيام' },
  { key: 'month', en: 'Month', ar: 'شهر' },
  { key: 'custom', en: 'Custom', ar: 'مخصص' },
];

type Preset = 'today' | 'week' | 'month' | 'custom';

function presetRange(p: Exclude<Preset, 'custom'>): { from: string; to: string } {
  const now = new Date();
  const today = toDateStr(now);
  if (p === 'today') return { from: today, to: today };
  if (p === 'week') { const f = new Date(now); f.setDate(f.getDate() - 6); return { from: toDateStr(f), to: today }; }
  const f = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: toDateStr(f), to: today };
}

export function ReportsView({ branchId }: { branchId: number }): JSX.Element {
  const { t, lang } = useLang();
  const [groupBy, setGroupBy] = useState<ReportGroupBy>('day');
  const [preset, setPreset] = useState<Preset>('month');
  const [custom, setCustom] = useState(() => presetRange('month'));

  const range = preset === 'custom' ? custom : presetRange(preset);

  const filtersQ = useQuery({ queryKey: ['resources', branchId], queryFn: () => api.resources(branchId) });
  const statusesQ = useQuery({ queryKey: ['statuses'], queryFn: api.statuses });
  const [employeeId, setEmployeeId] = useState<number | undefined>(undefined);
  const [statusId, setStatusId] = useState<number | undefined>(undefined);

  const q = useQuery({
    queryKey: ['report', branchId, groupBy, range.from, range.to, employeeId, statusId],
    queryFn: () => api.report(branchId, { group_by: groupBy, from: range.from, to: range.to, employee_id: employeeId, status_id: statusId }),
  });

  const exportCsv = (): void => {
    if (!q.data) return;
    downloadCsv(q.data, groupBy);
  };

  return (
    <div className="appt-scroll h-full overflow-auto p-3 sm:p-4">
      <div className="mx-auto max-w-6xl space-y-3 sm:space-y-4">
        {/* The controls wrap in a deliberate order on a phone: the title takes
            its own line, the dimension picker and the presets share the next,
            and the selects below stretch rather than sitting at their natural
            (illegibly narrow) widths. */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <h1 className="w-full text-lg font-bold text-slate-800 sm:w-auto sm:text-xl dark:text-slate-100">{t({ en: 'Reports', ar: 'التقارير' })}</h1>
          <select className="appt-input min-w-[130px] flex-1 py-1.5 sm:w-auto sm:flex-none" value={groupBy} onChange={(e) => setGroupBy(e.target.value as ReportGroupBy)}>
            {DIMENSIONS.map((d) => <option key={d.key} value={d.key}>{t({ en: d.en, ar: d.ar })}</option>)}
          </select>
          <div className="appt-seg">
            {PRESETS.map((p) => (
              <button key={p.key} aria-pressed={preset === p.key} onClick={() => setPreset(p.key)}>
                {t({ en: p.en, ar: p.ar })}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex w-full items-center gap-1 sm:w-auto">
              <input type="date" className="appt-input min-w-0 flex-1 py-1.5 sm:w-auto sm:flex-none" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
              <span className="flex-none text-slate-400">→</span>
              <input type="date" className="appt-input min-w-0 flex-1 py-1.5 sm:w-auto sm:flex-none" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
            </div>
          )}
          <button className="appt-btn-ghost ms-auto flex-none" onClick={exportCsv} disabled={!q.data || q.data.rows.length === 0}>{t({ en: 'Export CSV', ar: 'تصدير CSV' })}</button>
        </div>

        {/* filters */}
        <div className="flex flex-wrap gap-2">
          <select className="appt-input min-w-[140px] flex-1 py-1.5 sm:w-auto sm:flex-none" value={employeeId ?? ''} onChange={(e) => setEmployeeId(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">{t({ en: 'All employees', ar: 'كل الموظفين' })}</option>
            {(filtersQ.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <select className="appt-input min-w-[140px] flex-1 py-1.5 sm:w-auto sm:flex-none" value={statusId ?? ''} onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">{t({ en: 'All statuses', ar: 'كل الحالات' })}</option>
            {(statusesQ.data ?? []).map((s) => <option key={s.id} value={s.id}>{localName(lang, s.name, s.name_ar)}</option>)}
          </select>
        </div>

        {q.isLoading && <p className="py-10 text-center text-sm text-slate-400">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</p>}
        {q.data && <ReportTable report={q.data} groupBy={groupBy} />}
      </div>
    </div>
  );
}

function ReportTable({ report, groupBy }: { report: Report; groupBy: ReportGroupBy }): JSX.Element {
  const { t } = useLang();
  const statusName = useStatusName();
  const totals = report.totals;
  const firstColLabel = useMemo(() => t(COL_LABEL[groupBy]), [groupBy, t]);
  const label = (r: ReportRow): string => {
    if (groupBy === 'status') return statusName(Number(r.key), r.label);
    return r.label ?? (groupBy === 'employee' || groupBy === 'customer' ? (r.key === '0' ? '—' : `#${r.key}`) : r.key);
  };

  if (report.rows.length === 0) return <div className="appt-card p-10 text-center text-sm text-slate-400">{t({ en: 'No appointments in this period.', ar: 'لا توجد مواعيد في هذه الفترة.' })}</div>;

  return (
    <div className="appt-card overflow-hidden">
      {/* `overflow-x-auto` on its own did nothing here: a `w-full` table shrinks
          to its container rather than overflowing it, so eight columns just
          squashed into unreadable wrapped text on a phone. The min-width is
          what actually makes the scroll happen. */}
      <div className="appt-table-scroll">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/50">
            <tr>
              <Th className="text-start">{firstColLabel}</Th>
              <Th>{t({ en: 'Total', ar: 'الإجمالي' })}</Th>
              <Th>{t({ en: 'Completed', ar: 'مكتمل' })}</Th>
              <Th>{t({ en: 'Cancelled', ar: 'ملغي' })}</Th>
              <Th>{t({ en: 'No-show', ar: 'عدم حضور' })}</Th>
              <Th>{t({ en: 'Converted', ar: 'محوّل' })}</Th>
              <Th>{t({ en: 'Avg min', ar: 'متوسط الدقائق' })}</Th>
              <Th>{t({ en: 'Revenue', ar: 'الإيرادات' })}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {report.rows.map((r) => (
              <tr key={r.key} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                <Td className="text-start font-medium text-slate-800 dark:text-slate-100">{label(r)}</Td>
                <Td>{r.count}</Td>
                <Td className="text-emerald-600">{r.completed}</Td>
                <Td className="text-red-500">{r.cancelled}</Td>
                <Td className="text-orange-500">{r.no_show}</Td>
                <Td>{r.converted}</Td>
                <Td>{Math.round(r.avg_duration)}</Td>
                <Td className="font-semibold">{fmtMoney(r.revenue)}</Td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold dark:border-slate-700 dark:bg-slate-800/50">
            <tr>
              <Td className="text-start">{t({ en: 'Total', ar: 'الإجمالي' })}</Td>
              <Td>{totals.count}</Td><Td className="text-emerald-600">{totals.completed}</Td><Td className="text-red-500">{totals.cancelled}</Td>
              <Td className="text-orange-500">{totals.no_show}</Td><Td>{totals.converted}</Td><Td>{Math.round(totals.avg_duration)}</Td><Td>{fmtMoney(totals.revenue)}</Td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`px-3 py-2 text-end font-semibold ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-3 py-2 text-end tabular-nums ${className ?? ''}`}>{children}</td>;
}

function downloadCsv(report: Report, groupBy: ReportGroupBy): void {
  const header = [groupBy, 'total', 'completed', 'cancelled', 'no_show', 'converted', 'avg_duration_min', 'revenue'];
  const esc = (v: string | number): string => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(',')];
  for (const r of report.rows) {
    lines.push([r.label ?? r.key, r.count, r.completed, r.cancelled, r.no_show, r.converted, Math.round(r.avg_duration), fmtMoney(r.revenue)].map(esc).join(','));
  }
  lines.push(['TOTAL', report.totals.count, report.totals.completed, report.totals.cancelled, report.totals.no_show, report.totals.converted, Math.round(report.totals.avg_duration), fmtMoney(report.totals.revenue)].map(esc).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `appointments-${groupBy}-${report.from.slice(0, 10)}_${report.to.slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
